/**
 * Port of FinancialGoalService.requiredYield and .wealthProjection in the
 * backend: the plan drawn here must match the expectedAssets the API reports,
 * so a change on one side needs the same change on the other.
 *
 * One known difference: the backend steps whole years from the start month,
 * so from January until the month before a goal's start month its
 * expectedAssets stays at the previous year-end. This port walks calendar
 * years, so in those months the plan line runs above the API's Plan figure.
 *
 * Dates are yyyy-MM-dd strings handled as UTC calendar dates; a local time
 * zone would shift the start month (the v1 UI showed 09/2026 in October).
 */
export type GoalPlan = {
  startDate: string;
  endDate: string;
  initialWealth: number;
  savingsPerMonth: number;
  wealthTarget: number;
  adjustInflation: boolean;
  /** 0.02 is 2 % per year */
  inflationRate: number;
};

export type WealthProjectionItem = {
  /** Last day of the period, yyyy-MM-dd */
  date: string;
  wealth: number;
  savings: number;
  returns: number;
};

type CalendarDate = { year: number; month: number; day: number };

const DAY_MS = 86_400_000;
const YEAR_MS = 365 * DAY_MS;

const parseIsoDate = (iso: string): CalendarDate => {
  const [year, month, day] = iso.slice(0, 10).split("-").map(Number);
  return { year, month: month - 1, day };
};

const pad = (value: number) => String(value).padStart(2, "0");

export const toIsoDate = (year: number, month: number, day: number) => {
  const date = new Date(Date.UTC(year, month, day));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
    date.getUTCDate()
  )}`;
};

export const endOfMonthIso = (year: number, month: number) =>
  toIsoDate(year, month + 1, 0);

const fullYearsBetween = (first: CalendarDate, second: CalendarDate) => {
  const years = second.year - first.year;
  const beforeAnniversary =
    second.month < first.month ||
    (second.month === first.month && second.day < first.day);
  return beforeAnniversary ? years - 1 : years;
};

export const inflationAdjustedTarget = ({
  startDate,
  endDate,
  wealthTarget,
  adjustInflation,
  inflationRate,
}: GoalPlan) => {
  if (!adjustInflation) return wealthTarget;
  const years = fullYearsBetween(
    parseIsoDate(startDate),
    parseIsoDate(endDate)
  );
  return wealthTarget * Math.pow(1 + inflationRate, years);
};

const netPresentValue = (times: number[], cashFlows: number[], rate: number) =>
  cashFlows.reduce(
    (sum, cashFlow, index) =>
      sum + cashFlow * Math.pow(1 + rate, (times[0] - times[index]) / YEAR_MS),
    0
  );

/**
 * Bisection with the bracket and tolerances of the backend's
 * PerformanceKernel.irr, including its fallback of 0 when the bracket holds
 * no sign change.
 */
export const internalRateOfReturn = (times: number[], cashFlows: number[]) => {
  const npvAccuracy = 0.01;
  let low = -0.999999;
  let high = 1000;
  let npvLow = netPresentValue(times, cashFlows, low);
  const npvHigh = netPresentValue(times, cashFlows, high);

  if (Math.abs(npvLow) < npvAccuracy && Math.abs(npvHigh) < npvAccuracy) {
    return 0;
  }
  if (npvLow * npvHigh > 0) return 0;

  for (let iteration = 0; iteration < 100; iteration++) {
    const mid = (low + high) / 2;
    const npvMid = netPresentValue(times, cashFlows, mid);
    if (Math.abs(npvMid) < npvAccuracy || high - low < 1e-9) return mid;
    if (npvLow * npvMid < 0) {
      high = mid;
    } else {
      low = mid;
      npvLow = npvMid;
    }
  }
  return (low + high) / 2;
};

/**
 * Yield per year (0.05 is 5 %) that turns the initial wealth plus the monthly
 * savings into the (inflation-adjusted) target by the end date.
 */
export const requiredYield = (plan: GoalPlan) => {
  const start = parseIsoDate(plan.startDate);
  const end = parseIsoDate(plan.endDate);
  const endTime = Date.UTC(end.year, end.month, end.day);

  const times = [Date.UTC(start.year, start.month, 1) - DAY_MS];
  const cashFlows = [plan.initialWealth];

  if (plan.savingsPerMonth) {
    for (
      let month = start.month;
      Date.UTC(start.year, month, 1) <= endTime;
      month++
    ) {
      times.push(Date.UTC(start.year, month, 1));
      cashFlows.push(plan.savingsPerMonth);
    }
  }

  times.push(Date.UTC(end.year, end.month + 1, 0));
  cashFlows.push(-inflationAdjustedTarget(plan));

  return internalRateOfReturn(times, cashFlows);
};

/**
 * Wealth at the end of every calendar year from the start month to the end
 * month, with simple interest inside each year as in the backend. The first
 * and the last period may cover only part of a year.
 */
export const wealthProjection = (
  {
    startDate,
    endDate,
    initialWealth,
    savingsPerMonth,
  }: Pick<
    GoalPlan,
    "startDate" | "endDate" | "initialWealth" | "savingsPerMonth"
  >,
  yieldPerYear: number
): WealthProjectionItem[] => {
  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate);
  const items: WealthProjectionItem[] = [];
  let wealth = initialWealth;
  let savings = 0;

  for (let year = start.year; year <= end.year; year++) {
    const firstMonth = year === start.year ? start.month : 0;
    const lastMonth = year === end.year ? end.month : 11;
    const months = lastMonth - firstMonth + 1;
    if (months <= 0) break;

    const savingsThisYear = savingsPerMonth * months;
    wealth *= 1 + (yieldPerYear / 12) * months;
    wealth += savingsThisYear * (1 + (yieldPerYear / 12) * (months - 1));
    savings += savingsThisYear;

    items.push({
      date: endOfMonthIso(year, lastMonth),
      wealth,
      savings,
      returns: wealth - initialWealth - savings,
    });
  }
  return items;
};

/** Planned wealth at the end of the month of `date`, like expectedAssets. */
export const plannedWealthAt = (
  plan: Pick<GoalPlan, "startDate" | "initialWealth" | "savingsPerMonth">,
  yieldPerYear: number,
  date: string
) =>
  wealthProjection({ ...plan, endDate: date }, yieldPerYear).at(-1)?.wealth ??
  plan.initialWealth;
